import cx from 'classnames';
import PropTypes from 'prop-types';

import { PromptInstructions } from 'src/components/PromptInstructions';
import { EVENT_PAGE } from 'src/lib/events';
import { usePreparePromptInstructionsProps } from 'src/services/prompts/usePreparePromptInstructionsProps';

import { Actions } from './components/Actions';
import { PromptFooterActions } from './components/PromptFooterActions';
import { useIsEmptyPrompt } from './services/useIsEmptyPrompt';
import { usePromptFilesSupport } from './services/usePromptFilesSupport';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';
import { useViewState } from './useViewState';

const PLACEHOLDER =
  'Describe the content you want to create or select from one of the options below.';

export const NewProjectPromptWidget = ({ className, onFlowFinished, inPopupMode }) => {
  const styles = useStyles();

  const { promptText, classifyingTemplate, editorType } = useViewState();
  const isEmptyPrompt = useIsEmptyPrompt();

  const {
    // "images" handles
    uploadingImages,
    isUploadingImage,
    handleUploadImage,
    handleImageUploadSuccess,
    // "document" handles
    uploadingDocuments,
    isUploadingDocument,
    handleUploadDocument,
    handleDocumentUploadSuccess,
    // "action" handler
    hasUploadingResources,
    handlePromptInstructionsAction,
    handleDisplayError,
  } = usePromptFilesSupport();

  const promptInstructionsProps = usePreparePromptInstructionsProps({
    eventPage: EVENT_PAGE.homePage,
    uploadingImages,
    uploadingDocuments,
    provideUrlResources: true,
    provideDocumentResources: true,
    provideImageResources: true,
    setIsUploadingDocument: handleUploadDocument,
    onAction: handlePromptInstructionsAction,
    setIsUploadingImage: handleUploadImage,
    setDocumentError: handleDisplayError,
    setImageError: handleDisplayError,
    setDocumentUploadSuccess: handleDocumentUploadSuccess,
    setImageUploadSuccess: handleImageUploadSuccess,
  });

  const { handlePromptChange, handleWidgetAction, handleEditorChange } = useHandlers({
    onFlowFinished,
    inPopupMode,
  });

  return (
    <div
      className={cx(styles.root, className)}
      data-testid="newProjectPromptWidget"
    >
      <PromptInstructions
        className={styles.promptField}
        editableClasses={{ editable: styles.editable }}
        {...promptInstructionsProps}
        placeholder={PLACEHOLDER}
        value={promptText}
        onChange={handlePromptChange}
        uploadingImages={uploadingImages}
        isUploadingImage={isUploadingImage}
        uploadingDocuments={uploadingDocuments}
        isUploadingDocument={isUploadingDocument}
        editorDisabled={classifyingTemplate}
        footerActionsSlot={
          <PromptFooterActions
            onAction={handleWidgetAction}
            disabled={classifyingTemplate}
            loading={classifyingTemplate}
            allowedSubmit={!isEmptyPrompt}
            uploadingResources={hasUploadingResources}
            onEditorChange={handleEditorChange}
            editorType={editorType}
          />
        }
        hideActions
      />

      <Actions
        className={styles.actions}
        onAction={handleWidgetAction}
        disabled={classifyingTemplate}
      />
    </div>
  );
};

NewProjectPromptWidget.propTypes = {
  className: PropTypes.string,
  onFlowFinished: PropTypes.func,
  inPopupMode: PropTypes.bool,
};

NewProjectPromptWidget.defaultProps = {
  className: null,
  onFlowFinished: null,
  inPopupMode: null,
};
